import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, LoadingSkeleton, DeniedState } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { SeverityBadge } from "@/components/Badges";
import { ArrowRightLeft, Building2 } from "lucide-react";

export default function Portfolio() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/portfolio").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, []);

  if (!user?.is_super_admin) return <div className="max-w-7xl mx-auto px-6 py-10"><DeniedState requiredRoles={["Super Admin"]} /></div>;
  if (loading || !data) return <div className="p-8"><LoadingSkeleton /></div>;

  const t = data.totals;
  const KPIS = [
    { label: "Customers", v: t.tenants }, { label: "Users", v: t.users },
    { label: "Risks (open)", v: t.risks }, { label: "Critical", v: t.critical, tone: "rose" },
    { label: "High", v: t.high, tone: "orange" }, { label: "Appetite breaches", v: t.breaches, tone: "amber" },
    { label: "Open findings", v: t.open_findings }, { label: "Pending acceptances", v: t.pending_acceptances },
  ];

  const switchTo = async (tid) => {
    try { await api.post(`/admin/switch-tenant?tenant_id=${tid}`); toast.success("Switched"); await refresh(); nav("/dashboard"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader kicker="Super admin · cross-customer" title="Portfolio" description="Risk posture across every customer tenant, in one view." />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {KPIS.map((k) => (
          <Card key={k.label} className="p-4" data-testid={`portfolio-kpi-${k.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
            <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 font-semibold">{k.label}</div>
            <div className="font-display font-extrabold text-3xl tabular-nums text-slate-900 mt-1">{k.v}</div>
          </Card>
        ))}
      </div>
      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b"><tr className="text-left">
            {["Customer", "Users", "Risks", "Critical", "High", "Breaches", "Findings", "Pending", ""].map((h) => <th key={h} className="px-3 py-2.5 text-[10px] uppercase tracking-widest font-mono font-semibold text-slate-500">{h}</th>)}
          </tr></thead>
          <tbody>
            {data.tenants.map((t) => (
              <tr key={t.id} className="border-b border-slate-100 hover:bg-cyan-50/30" data-testid={`portfolio-row-${t.slug}`}>
                <td className="px-3 py-2.5"><div className="flex items-center gap-2"><span className="h-3 w-3 rounded" style={{ background: t.primary_color }} /><span className="font-semibold text-slate-900">{t.name}</span></div></td>
                <td className="px-3 py-2.5 font-mono tabular-nums">{t.users}</td>
                <td className="px-3 py-2.5 font-mono tabular-nums font-bold">{t.risks}</td>
                <td className="px-3 py-2.5">{t.critical > 0 ? <SeverityBadge severity="Critical" /> : <span className="text-slate-400 font-mono">0</span>}<span className="ml-1 font-mono tabular-nums">{t.critical}</span></td>
                <td className="px-3 py-2.5 font-mono tabular-nums">{t.high}</td>
                <td className="px-3 py-2.5 font-mono tabular-nums text-amber-700 font-semibold">{t.breaches}</td>
                <td className="px-3 py-2.5 font-mono tabular-nums">{t.open_findings}</td>
                <td className="px-3 py-2.5 font-mono tabular-nums">{t.pending_acceptances}</td>
                <td className="px-3 py-2.5"><Button size="sm" variant="outline" onClick={() => switchTo(t.id)} data-testid={`portfolio-switch-${t.slug}`}><ArrowRightLeft className="h-3.5 w-3.5 mr-1" /> Open</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
